import type { IGetDescriptionRequestParams, IDescriptions } from './types'
import {
  getEmptyDescriptionsTemplate,
  convertDescriptionResponseToObject,
  getJamsBackendApi,
  fetchWithTimeout,
  getDescriptionsApi,
  getJueApi,
  getDescriptionApiURL,
} from './utils'
import type {
  IJAMSAuthResponse,
  IGenerateDescriptionRequestParams,
  IGetDescriptionsRequestParams,
  IUpdateDescriptionRequestParams,
  IAccountSettings,
  IBackendPrompt,
  IAccountPrompts,
  IPromptCreateRequest,
  IPromptUpdateRequest,
  IPromptsApiResponse,
  IPromptApiResponse,
  PromptType,
  IBackendGuardrail,
  IGlobalGuardrails,
  IGuardrailCreateRequest,
  IGuardrailUpdateRequest,
  IGuardrailsApiResponse,
  IGuardrailApiResponse,
  GuardrailType,
} from './types'
import axios from 'axios'
import type { IApiProductSettings } from './types'
import { useAccountSettingsStore } from './stores/accountSettings'

const getOptionalAuthHeaders = () => {
  try {
    const store = useAccountSettingsStore()
    const tok = String(store?.sessionToken || '').trim()
    return tok ? { Authorization: `Bearer ${tok}` } : {}
  } catch {
    return {}
  }
}

export const getAccountSettings = async (accountId: string): Promise<IAccountSettings | null> => {
  const endpoint = getJamsBackendApi()
  const path = `account?accountName=${accountId}`

  try {
    const response = await axios.get(`${endpoint}/${path}`)
    const responseJson: any = response.data
    console.info('aie_descriptions', 'getAccountSettings', responseJson)
    return responseJson
  } catch (error: any) {
    if (error.response && error.response.status) {
      return null
    }
    console.error('aie_descriptions', 'getAccountSettings', error)
    return null
  }
}

export const fetchVehicles = async (accountId: string): Promise<any> => {
  const endpoint = getDescriptionsApi()
  const path = 'get-description-keys'
  const query = `accountId=${accountId}`

  try {
    const response = await axios.get(`${endpoint}/${path}?${query}`)
    return response.data
  } catch (error: any) {
    throw new Error(
      `Failed to fetch vehicle data: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

export const refreshVehicles = async (params: IGetDescriptionsRequestParams): Promise<boolean> => {
  try {
    const apiUrl = getDescriptionsApi()
    const path = 'generate-description-keys'

    await axios.post(`${apiUrl}/${path}`, params, {
      timeout: 60000,
      headers: { ...getOptionalAuthHeaders() },
    })
    return true
  } catch (error) {
    console.error('Error fetching API endpoint:', error)
  }
  return false
}

export const fetchVehicleDescription = async (
  params: IGetDescriptionsRequestParams,
): Promise<IDescriptions> => {
  const apiUrl = getDescriptionsApi()
  const path = 'get-descriptions'

  const accountId = params.accountId
  const vin = params.vin
  const query = `accountId=${accountId}&vin=${vin}`

  let descriptions = getEmptyDescriptionsTemplate()

  try {
    const response = await axios.get(`${apiUrl}/${path}?${query}`)
    const responseJson = response.data
    descriptions = convertDescriptionResponseToObject(responseJson)
  } catch (error) {
    console.error('Error fetching API endpoint:', error)
    throw error
  }

  return descriptions
}

export const fetchNewVehicleDescription = async (
  params: IGenerateDescriptionRequestParams,
): Promise<IDescriptions> => {

  const apiUrl = getDescriptionsApi()
  const path = 'generate-description'

  try {
    const response = await axios.post(`${apiUrl}/${path}`, params, {
      headers: { ...getOptionalAuthHeaders() },
    })
    return response.data
  } catch (err: any) {
    throw new Error(`API call to '/generate-description' failed. ${err.message}`)
  }
}

export const generateContent = async (params: { accountId: string; vinList?: string[] }) => {
  const apiUrl = getDescriptionsApi()
  const path = 'orchestrator/trigger'

  const body: any = {
    triggerType: 'single-account',
    accountId: params.accountId,
  }
  if (params.vinList && params.vinList.length > 0) {
    body.vinList = params.vinList
  }

  await axios.post(`${apiUrl}/${path}`, body, {
    timeout: 15000,
    headers: { ...getOptionalAuthHeaders() },
  })
  return true
}

export const verifyJAMSToken = async (token: string): Promise<IJAMSAuthResponse | null> => {
  const endpoint = getJamsBackendApi()
  const path = `account/user/verify`

  try {
    const response = await axios.get(`${endpoint}/${path}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    return response.data as IJAMSAuthResponse
  } catch (error: any) {
    throw new Error('Invalid token')
  }
}

export const verifyJAMSAdminToken = async (token: string): Promise<IJAMSAuthResponse | null> => {
  const endpoint = getJamsBackendApi()
  const path = `admin/verify`

  try {
    const response = await axios.get(`${endpoint}/${path}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    return response.data as IJAMSAuthResponse
  } catch (error: any) {
    return null
  }
}

export const logoutUser = async (token: string) => {
  const endpoint = getJamsBackendApi()
  const path = `user/logout`

  await axios.get(`${endpoint}/${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

export const updateVehicleDescription = async (
  params: IUpdateDescriptionRequestParams,
): Promise<IDescriptions> => {
  const endpoint = getDescriptionsApi()
  const path = 'update-description'

  try {
    const response = await axios.post(`${endpoint}/${path}`, params, {
      headers: { ...getOptionalAuthHeaders() },
    })
    return response.data
  } catch (err: any) {
    throw new Error(`API call to '/update-vehicle-description failed'. ${err.message}`)
  }
}

// Lock/unlock description for a VIN (no content changes required)
export const setDescriptionLock = async (
  accountId: string,
  vin: string,
  descriptionLocked: boolean,
): Promise<boolean> => {
  const endpoint = getDescriptionsApi()
  const path = 'update-description'

  try {
    await axios.post(
      `${endpoint}/${path}`,
      { accountId, vin, descriptionLocked },
      { headers: { ...getOptionalAuthHeaders() } },
    )
    return true
  } catch (err: any) {
    console.error('Failed to set description lock', err)
    return false
  }
}

export const getVehiclesFromJUE = async (
  juid: string,
  values: string[] = ['VIN'],
): Promise<any[]> => {
  const base = getJueApi()
  if (!base) throw new Error('VITE_JUE_API_BASE_URL is not configured')

  const url = `${base.replace(/\/$/, '')}/jue/${encodeURIComponent(juid)}`
  const cancelTokenSource = axios.CancelToken.source()
  const timeoutId = setTimeout(() => cancelTokenSource.cancel('Overall request timed out'), 30_000)

  try {
    const response = await axios.get(url, {
      params: { values: values.join(',') },
      cancelToken: cancelTokenSource.token,
    })
    const data = response?.data
    // Heavy payload fallback
    if (data && data.data === null && data?.metadata?.s3Url) {
      const s3Url: string = data.metadata.s3Url as string
      const s3Res = await axios.get(s3Url)
      const s3Json = s3Res?.data
      const s3Items = Array.isArray(s3Json)
        ? s3Json
        : Array.isArray(s3Json?.items)
        ? s3Json.items
        : Array.isArray(s3Json?.data)
        ? s3Json.data
        : []

      const mapItem = (it: any) => {
        const out: any = { vin: it?.VIN || it?.vin || '' }
        const year = it?.Year ?? it?.year
        const make = it?.Make ?? it?.make
        const model = it?.Model ?? it?.model
        const trim = it?.Trim ?? it?.trim
        const stock = it?.Stock ?? it?.stock_number ?? it?.stockNumber
        const dealerDesc = it?.Description ?? it?.dealer_description ?? it?.dealerDescription
        if (year != null) out.year = String(year)
        if (make != null) out.make = String(make)
        if (model != null) out.model = String(model)
        if (trim != null) out.trim = String(trim)
        if (stock != null) out.stock_number = String(stock)
        if (dealerDesc != null) out.dealer_description = String(dealerDesc)
        return out
      }
      return s3Items.map(mapItem)
    }
    const items = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.data)
      ? data.data
      : []

    const mapItem = (it: any) => {
      const out: any = { vin: it?.VIN || '' }
      const year = it?.Year
      const make = it?.Make
      const model = it?.Model
      const trim = it?.Trim
      const stock = it?.Stock
      const dealerDesc = it?.Description
      if (year != null) out.year = String(year)
      if (make != null) out.make = String(make)
      if (model != null) out.model = String(model)
      if (trim != null) out.trim = String(trim)
      if (stock != null) out.stock_number = String(stock)
      if (dealerDesc != null) out.dealer_description = String(dealerDesc)
      return out
    }
    return items.map(mapItem)
  } finally {
    clearTimeout(timeoutId)
  }
}

export const getProductSettings = async (
  accountId: string,
  productId: string,
): Promise<IApiProductSettings | null> => {
  const endpoint = getJamsBackendApi()
  const path = `account/products/${accountId}/${productId}`

  try {
    const response = await axios.get(`${endpoint}/${path}`)
    const responseJson: any = response.data
    console.info('micro_srp', 'getProductSettings', responseJson)
    return responseJson
  } catch (error) {
    console.error('micro_srp', 'getProductSettings', error)
    return null
  }
}

export const updateProductSettings = async (
  accountId: string,
  productId: string,
  settings: IApiProductSettings,
  isAdmin: boolean = false,
  token: string = '',
): Promise<boolean> => {
  const endpoint = getJamsBackendApi()
  const path = isAdmin
    ? `account/products/${accountId}/${productId}`
    : `client/account/products/${accountId}/${productId}`

  try {
    const response = await axios.put(
      `${endpoint}/${path}`,
      { config: settings },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.status >= 200 && response.status < 300
  } catch (error) {
    console.error('autofocus_highlights', 'updateProductSettings', error)
    return false
  }
}

// Prompt API functions
export const fetchPrompts = async (
  accountName: string,
  type?: PromptType,
): Promise<IBackendPrompt[]> => {
  const path = `prompts/${accountName}${type ? `?type=${type}` : ''}`
  const url = getDescriptionApiURL(path)

  try {
    const response = await axios.get(url)
    const data: IPromptsApiResponse = response.data
    return data.prompts || []
  } catch (error) {
    console.error('Error fetching prompts:', error)
    throw error
  }
}

export const createPrompt = async (
  accountName: string,
  prompt: IPromptCreateRequest,
): Promise<IBackendPrompt> => {
  const path = `prompts/${accountName}`
  const url = getDescriptionApiURL(path)

  try {
    const response = await axios.post(url, prompt)

    const data: IPromptApiResponse = response.data
    return data.prompt
  } catch (error) {
    console.error('Error creating prompt:', error)
    throw error
  }
}

export const updatePrompt = async (
  accountName: string,
  promptId: string,
  updates: IPromptUpdateRequest,
): Promise<IBackendPrompt> => {
  const path = `prompts/${accountName}/${promptId}`
  const url = getDescriptionApiURL(path)

  try {
    const response = await axios.patch(url, updates)

    const data: IPromptApiResponse = await response.data
    return data.prompt
  } catch (error) {
    console.error('Error updating prompt:', error)
    throw error
  }
}

export const deletePrompt = async (
  accountName: string,
  promptId: string,
): Promise<IBackendPrompt> => {
  const path = `prompts/${accountName}/${promptId}`
  const url = getDescriptionApiURL(path)

  try {
    const response = await axios.delete(url)
    const data: IPromptApiResponse = response.data
    return data.prompt
  } catch (error) {
    console.error('Error deleting prompt:', error)
    throw error
  }
}

// Baseline Prompt API functions
export const getBaselinePrompt = async (
  accountName: string,
  type: 'description' | 'highlight',
): Promise<string> => {
  const path = `prompts/baseline/${accountName}/${type}`
  const url = getDescriptionApiURL(path)
  const res = await axios.get(url)
  return String(res?.data?.content || '')
}

export const putBaselinePrompt = async (
  accountName: string,
  type: 'description' | 'highlight',
  content: string,
): Promise<boolean> => {
  const path = `prompts/baseline/${accountName}/${type}`
  const url = getDescriptionApiURL(path)
  const res = await axios.put(url, { content })
  return res.status >= 200 && res.status < 300
}

// New: Account Instructions API (preferred)
export const getAccountInstructions = async (
  accountName: string,
  type: 'description' | 'highlight',
): Promise<string> => {
  const path = `prompts/account/${accountName}/${type}`
  const url = getDescriptionApiURL(path)
  const res = await axios.get(url)
  return String(res?.data?.content || '')
}

export const putAccountInstructions = async (
  accountName: string,
  type: 'description' | 'highlight',
  content: string,
): Promise<boolean> => {
  const path = `prompts/account/${accountName}/${type}`
  const url = getDescriptionApiURL(path)
  const res = await axios.put(url, { content })
  return res.status >= 200 && res.status < 300
}

// New: System Instructions API
export const getSystemInstructions = async (
  type: 'description' | 'highlight',
): Promise<string> => {
  const path = `prompts/system/${type}`
  const url = getDescriptionApiURL(path)
  const res = await axios.get(url)
  return String(res?.data?.content || '')
}

export const putSystemInstructions = async (
  type: 'description' | 'highlight',
  content: string,
): Promise<boolean> => {
  const path = `prompts/system/${type}`
  const url = getDescriptionApiURL(path)
  const res = await axios.put(url, { content })
  return res.status >= 200 && res.status < 300
}

// Account Instructions CRUD
export interface IAccountInstructionApiItem { id: string; title: string; content: string; type: 'description'|'highlight'; isActive: boolean }
export interface IAccountInstructionsApiList { instructions: IAccountInstructionApiItem[] }
export interface IAccountInstructionApiOne { instruction: IAccountInstructionApiItem }

export const listAccountInstructions = async (accountName: string, type?: 'description'|'highlight'): Promise<IAccountInstructionApiItem[]> => {
  const accountSettingStore = useAccountSettingsStore()
  const url = getDescriptionApiURL(`account-instructions/${accountName}`)
  const res = await axios.get(`${url}${type ? `?type=${type}` : ''}`, { headers: { Authorization: `Bearer ${accountSettingStore.sessionToken}` }})
  return (res.data as IAccountInstructionsApiList).instructions || []
}

export const createAccountInstruction = async (accountName: string, item: { title: string; content: string; type: 'description'|'highlight'; isActive?: boolean }): Promise<IAccountInstructionApiItem> => {
  const accountSettingStore = useAccountSettingsStore()
  const url = getDescriptionApiURL(`account-instructions/${accountName}`)
  const res = await axios.post(url, item, { headers: { Authorization: `Bearer ${accountSettingStore.sessionToken}` }})
  return (res.data as IAccountInstructionApiOne).instruction
}

export const updateAccountInstruction = async (accountName: string, id: string, updates: Partial<{ title: string; content: string; isActive: boolean }>): Promise<IAccountInstructionApiItem> => {
  const accountSettingStore = useAccountSettingsStore()
  const url = getDescriptionApiURL(`account-instructions/${accountName}/${id}`)
  const res = await axios.patch(url, updates, { headers: { Authorization: `Bearer ${accountSettingStore.sessionToken}` }})
  return (res.data as IAccountInstructionApiOne).instruction
}

export const deleteAccountInstruction = async (accountName: string, id: string): Promise<IAccountInstructionApiItem> => {
  const accountSettingStore = useAccountSettingsStore()
  const url = getDescriptionApiURL(`account-instructions/${accountName}/${id}`)
  const res = await axios.delete(url, { headers: { Authorization: `Bearer ${accountSettingStore.sessionToken}` }})
  return (res.data as IAccountInstructionApiOne).instruction
}

// New: System Instructions CRUD (list/create/update/delete)
export interface ISystemInstructionApiItem { id: string; title: string; content: string; type: 'description'|'highlight'; isActive: boolean }
export interface ISystemInstructionsApiList { instructions: ISystemInstructionApiItem[] }
export interface ISystemInstructionApiOne { instruction: ISystemInstructionApiItem }

export const listSystemInstructions = async (type?: 'description'|'highlight'): Promise<ISystemInstructionApiItem[]> => {
  const accountSettingStore = useAccountSettingsStore()
  const url = getDescriptionApiURL('system-instructions')
  const res = await axios.get(`${url}${type ? `?type=${type}` : ''}` , {
    headers: { Authorization: `Bearer ${accountSettingStore.sessionToken}` },
  })
  return (res.data as ISystemInstructionsApiList).instructions || []
}

export const createSystemInstruction = async (item: { title: string; content: string; type: 'description'|'highlight'; isActive?: boolean }): Promise<ISystemInstructionApiItem> => {
  const accountSettingStore = useAccountSettingsStore()
  const url = getDescriptionApiURL('system-instructions')
  const res = await axios.post(url, item, {
    headers: { Authorization: `Bearer ${accountSettingStore.sessionToken}` },
  })
  return (res.data as ISystemInstructionApiOne).instruction
}

export const updateSystemInstruction = async (id: string, updates: Partial<{ title: string; content: string; isActive: boolean }>): Promise<ISystemInstructionApiItem> => {
  const accountSettingStore = useAccountSettingsStore()
  const url = getDescriptionApiURL(`system-instructions/${id}`)
  const res = await axios.patch(url, updates, {
    headers: { Authorization: `Bearer ${accountSettingStore.sessionToken}` },
  })
  return (res.data as ISystemInstructionApiOne).instruction
}

export const deleteSystemInstruction = async (id: string): Promise<ISystemInstructionApiItem> => {
  const accountSettingStore = useAccountSettingsStore()
  const url = getDescriptionApiURL(`system-instructions/${id}`)
  const res = await axios.delete(url, {
    headers: { Authorization: `Bearer ${accountSettingStore.sessionToken}` },
  })
  return (res.data as ISystemInstructionApiOne).instruction
}

export const fetchPromptById = async (
  accountName: string,
  promptId: string,
): Promise<IBackendPrompt> => {
  const path = `prompts/${accountName}/${promptId}`
  const url = getDescriptionApiURL(path)

  try {
    const response = await axios.get(url)
    const data: IPromptApiResponse = response.data
    return data.prompt
  } catch (error) {
    console.error('Error fetching prompt by ID:', error)
    throw error
  }
}

// Guardrail API functions
export const fetchGuardrails = async (type?: GuardrailType): Promise<IBackendGuardrail[]> => {
  const accountSettingStore = useAccountSettingsStore()
  const path = `guardrails`
  const url = getDescriptionApiURL(path)
  const queryParams = type ? `?type=${type}` : ''

  try {
    const response = await axios.get(`${url}${queryParams}`, {
      headers: {
        Authorization: `Bearer ${accountSettingStore.sessionToken}`,
      },
    })
    const data: IGuardrailsApiResponse = response.data
    return data.guardrails || []
  } catch (error) {
    console.error('Error fetching guardrails:', error)
    throw error
  }
}

export const createGuardrail = async (
  guardrail: IGuardrailCreateRequest,
): Promise<IBackendGuardrail> => {
  const accountSettingStore = useAccountSettingsStore()
  const path = `guardrails`
  const url = getDescriptionApiURL(path)

  try {
    const response = await axios.post(url, guardrail, {
      headers: {
        Authorization: `Bearer ${accountSettingStore.sessionToken}`,
      },
    })

    const data: IGuardrailApiResponse = response.data
    return data.guardrail
  } catch (error) {
    console.error('Error creating guardrail:', error)
    throw error
  }
}

export const updateGuardrail = async (
  guardrailId: string,
  updates: IGuardrailUpdateRequest,
): Promise<IBackendGuardrail> => {
  const accountSettingStore = useAccountSettingsStore()
  const path = `guardrails/${guardrailId}`
  const url = getDescriptionApiURL(path)

  try {
    const response = await axios.patch(url, updates, {
      headers: {
        Authorization: `Bearer ${accountSettingStore.sessionToken}`,
      },
    })

    const data: IGuardrailApiResponse = await response.data
    return data.guardrail
  } catch (error) {
    console.error('Error updating guardrail:', error)
    throw error
  }
}

export const deleteGuardrail = async (guardrailId: string): Promise<IBackendGuardrail> => {
  const accountSettingStore = useAccountSettingsStore()
  const path = `guardrails/${guardrailId}`
  const url = getDescriptionApiURL(path)

  try {
    const response = await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${accountSettingStore.sessionToken}`,
      },
    })
    const data: IGuardrailApiResponse = response.data
    return data.guardrail
  } catch (error) {
    console.error('Error deleting guardrail:', error)
    throw error
  }
}

export const fetchGuardrailById = async (guardrailId: string): Promise<IBackendGuardrail> => {
  const accountSettingStore = useAccountSettingsStore()
  const path = `guardrails/${guardrailId}`
  const url = getDescriptionApiURL(path)

  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${accountSettingStore.sessionToken}`,
      },
    })
    const data: IGuardrailApiResponse = response.data
    return data.guardrail
  } catch (error) {
    console.error('Error fetching guardrail by ID:', error)
    throw error
  }
}
